import NextImage from "@/components/shared/NextImage";
import { Post } from "@/services/ContentsService";
import { useTrans } from "@/utils/useTrans";
import PlayCircleIcon from "@/v2-components/core/icon/PlayCircleIcon";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/v2-components/ui/carousel";
import React from "react";

interface Props {
  posts: Post[];
}

export default function ReviewVideos({ posts }: Props) {
  const { t } = useTrans(["games"]);
  return (
    <div>
      {/**
       * 시안 `29889:101539` — 제목(h32) → 목록 y=52 이므로 **갭 20**(종전 32).
       * ★좌표(52)를 그대로 갭으로 쓰면 안 된다 — 앞 요소 높이를 빼야 한다.
       *
       * ★**색이 폭마다 다르다** — 모바일 `30035:21506` `#21272a`(cg-90) ·
       * 데스크탑 `29889:101540` `#121619`(cg-100). 종전엔 색 지정이 없어 두 폭 다
       * 상속값(cg-90)이었다.
       */}
      <h2 className="mx-4 mb-5 text-2xl laptop:mx-0 laptop:text-3xl laptop:text-coolgray-100">
        {t(`Review Videos`) + ` (${posts.length})`}
      </h2>
      <Carousel
        opts={{
          align: "start",
          slidesToScroll: 1,
        }}
        className="w-full"
      >
        {/* 시안 `29889:101541` — 카드 304 · 간격 **20** (304×3 + 20×2 = 952). 종전 16. */}
        <CarouselContent className="ml-4 laptop:-ml-5">
          {posts.map((post, index) => (
            <CarouselItem
              key={index}
              /**
               * 데스크탑 시안 `29889:101541` — **3장/줄**(304×3 + 20×2 = 952).
               *
               * ★**모바일 카드 폭이 형제 섹션과 다르다** — 시안이 그렇게 그렸다:
               *
               * ```
               *   리뷰 영상    30035:21510  Viewer 242×135   ← 이 파일
               *   비슷한 게임   30035:21556  카드   300       ← RecommendedGames
               * ```
               *
               * 종전엔 둘 다 `max-w-[90%]` 였고, 그 값은 **「비슷한 게임」에만 맞았다**
               * (실측 294 ≈ 시안 300). 리뷰 영상은 264.8 이 나와 시안보다 **9.4% 넓었다**.
               * 그만큼 다음 카드가 덜 보인다(peek 이 줄어든다).
               *
               * ★**「둘」이 아니라 셋이다 — 세 번째는 죽어 있다** (#5511 R2 m2).
               * `games/detail` 의 `max-w-[90%]` 를 전수로 세면 코드 3자리다:
               *
               * ```
               *   RecommendedGames:66  비슷한 게임 본체    실측 294  ≈ 시안 300  ✅
               *   RecommendedGames:47  같은 파일 스켈레톤   본체와 같은 값(의도)
               *   SupportedDevices:50  지원 기기 레일      ★어느 구간에서도 구속 안 함
               * ```
               *
               * 세 번째는 **死코드**다(실측 2026-09-03): 그 캐러셀이 `hidden … smd:block`
               * 이라 360 에서는 렌더되지 않고(그 아래는 `grid-cols-2`), 768 에서는
               * `smd:basis-1/4`(188 = 트랙 752÷4)가, 1440 에서는 `laptop:basis-1/5`
               * (284.8 = 1424÷5)가 이긴다. `90%` 는 각각 676.8 · 1281.6 이라 닿지 않는다.
               * 그래서 「한 값이 형제로 복사돼 한 곳에만 맞았다」는 진단이 그 자리에는
               * **결함을 만들지 않는다** — 지우는 것은 이 PR 범위 밖이라 두었다.
               *
               * `83%` 는 실측으로 맞춘 값이다 — 360 에서 트랙 312 × 0.83 = 258.96,
               * 여기서 `pr-4`(16)를 빼면 썸네일 **≈243** 이다(시안 242).
               *
               * ⚠️ `max-w` 는 **모바일에서만 구속력이 있다.** 1440 에서는 `basis-1/3`
               * (317)이 이겨서 이 값이 데스크탑을 건드리지 않는다 — 실측으로 확인했다.
               */
              className="basis max-w-[83%] cursor-pointer pl-0 pr-4 smd:basis-1/3 laptop:basis-1/3 laptop:pl-5 laptop:pr-0"
              onClick={() => {
                /**
                 * ★`"__blank"`(언더바 2개)였다 — **오타이고 동작이 다르다.**
                 *
                 * `_blank` 는 예약어지만 `__blank` 는 **이름 있는 창**이라, 두 번째
                 * 카드를 누르면 새 탭이 아니라 **첫 영상을 열었던 창을 덮어쓴다.**
                 * 리포 형제(`menu-view.tsx` 의 새 탭 메뉴)가 `"_blank", "noopener,noreferrer"`
                 * 다 — 그 표기를 따른다. (작성 당시 형제가 하나 더 있었는데, 그
                 * ~~`help-center-search-no-data.tsx`~~ 는 렌더 경로가 없어 2026-10-01 에 지웠다.)
                 *
                 * `noopener` 는 열린 탭이 `window.opener` 로 이 페이지를 조작하는 것을 막는다.
                 */
                window.open(post.link, "_blank", "noopener,noreferrer");
              }}
            >
              <div className="relative aspect-video overflow-hidden rounded-lg">
                <NextImage
                  src={post.thumbnailLink}
                  alt="video thumbnail"
                  className="absolute object-cover"
                  sizes={"(max-width: 1023px) 90vw, 400px"}
                  fill
                />
                {/**
                 * ★**재생 어포던스를 되살렸다** — 시안 `30035:21511` 48px 가운데.
                 *
                 * 카드를 누르면 YouTube 가 새 창으로 열리는데(`window.open(post.link)`)
                 * 종전엔 썸네일만 있어 **정지 이미지로 보였다.**
                 *
                 * ⚠️ 데스크탑 시안(`29889:101543`)은 이 자리가 **회색 사각형
                 * 플레이스홀더**(`bg-#697077`)라 썸네일 자체가 없다 — 오버레이가 없는
                 * 것이 아니라 **그릴 대상이 없는 것**이다. 「데스크탑엔 없다」로 읽으면
                 * 플레이스홀더를 명세로 오독하게 되므로 **두 폭 모두** 둔다.
                 */}
                <PlayCircleIcon
                  className="pointer-events-none absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-black invert"
                  aria-hidden
                />
              </div>
              {/**
               * 메타 행 — 시안 모바일 `30035:21512` · 데스크탑 `29889:101544`.
               *
               * ```
               *                    모바일 30035:21504    데스크탑 29889:101542
               *   아바타            36                    44
               *   아바타↔텍스트      ★8                   12
               *   텍스트 줄 간격     ★2                   8
               *   제목              ★14/18 SemiBold .25   16/22 Medium .15
               *   둘째 줄            ★12/16 .4            14/18 .25
               * ```
               *
               * 종전엔 데스크탑 값만 두고 모바일에 그대로 흘렸다(아바타 크기만 갈라져 있었다).
               */}
              <div className="mt-3 flex gap-2 laptop:gap-3">
                <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full laptop:h-11 laptop:w-11">
                  <NextImage
                    src={post.authorThumbnail}
                    alt="author thumbnail"
                    className="absolute object-cover"
                    sizes={"100px"}
                    fill
                  />
                </div>
                <div className="flex flex-col gap-0.5 laptop:gap-2">
                  <p className="line-clamp-2 text-base font-600 tracking-[0.25px] laptop:text-lg laptop:font-500 laptop:tracking-[0.15px]">
                    {post.title}
                  </p>
                  {/**
                   * ⚠️ **시안의 이 슬롯은 `name + views` 다**(데스크탑 노드 이름이
                   * 그대로 `name + views` · 모바일은 `128 views`). 지금은 **이름만** 그린다 —
                   * `Post` 에 조회수 필드가 없다(`author`·`authorThumbnail`·`title`·
                   * `type`·`link`·`thumbnailLink` 뿐).
                   *
                   * 붙이려면 VR Overview 가 쓰는 `/api/games/youtube-videos` 를 재사용해
                   * `post.link` 에서 영상 id 를 뽑아 조회수를 받아야 한다 — **네트워크
                   * 의존이 늘어나는 별건**이라 이 PR 에서는 하지 않았다.
                   */}
                  <p className="text-sm tracking-[0.4px] text-coolgray-70 laptop:text-base laptop:tracking-[0.25px]">
                    {post.author}
                  </p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="invisible top-[38%] translate-x-1/3 disabled:invisible laptop:visible max:-translate-x-1/2" />
        <CarouselNext className="invisible top-[38%] -translate-x-1/3 disabled:invisible laptop:visible max:translate-x-1/2" />
      </Carousel>
    </div>
  );
}
